import { browser } from '$app/env';
import { useFetchClient } from '@foundatiofx/fetchclient';
import { get } from 'svelte/store';
import { currentUser } from '#api/auth.ts';
import { isProd } from '#lib/stores/stores.ts';
import { timeTravel } from '#lib/services/time.ts';

type ClockStatus = {
    isTimeTraveling: boolean;
    utcNow: string;
};

let refreshing = false;
let pending = false;

/**
 * Re-discovers whether the current client scope is time-traveling and caches the result into the
 * {@link timeTravel} store. No-op in production: the /clock endpoints don't exist there, so we never
 * call them and always fall back to real browser time.
 *
 * A call made while another is in flight coalesces into a single trailing run, so a client switch
 * mid-fetch still ends on the current scope's state rather than the stale scope the in-flight call
 * was resolving.
 */
export async function refreshTimeTravel(): Promise<void> {
    if (get(isProd)) {
        timeTravel.set({ traveling: false, frozenUtcMs: null });
        return;
    }

    if (refreshing) {
        pending = true;
        return;
    }

    refreshing = true;
    try {
        do {
            pending = false;
            const result = await useFetchClient().getJSON<ClockStatus>('utility/clock/status');
            if (result.ok && result.data?.isTimeTraveling) {
                timeTravel.set({ traveling: true, frozenUtcMs: new Date(result.data.utcNow).getTime() });
            } else {
                timeTravel.set({ traveling: false, frozenUtcMs: null });
            }
        } while (pending);
    } catch {
        // Endpoint absent (prod) or a transient failure → fall back to real browser time.
        timeTravel.set({ traveling: false, frozenUtcMs: null });
    } finally {
        refreshing = false;
        pending = false;
    }
}

let initialized = false;

/**
 * Wires the discovery triggers once at app startup. Skipped entirely in production, so no polling or
 * API calls happen there. Elsewhere it re-checks on client switch, on tab focus, and on a low-frequency
 * poll — the server clock is frozen, so this only needs to catch a tester moving or clearing it.
 */
export function initTimeTravel(): void {
    if (initialized || !browser || get(isProd)) return;
    initialized = true;

    let lastClientId: number | null | undefined;
    currentUser.subscribe((user) => {
        if (user?.clientId !== lastClientId) {
            lastClientId = user?.clientId;
            void refreshTimeTravel();
        }
    });

    window.addEventListener('focus', () => void refreshTimeTravel());
    setInterval(() => void refreshTimeTravel(), 60_000);
}
